import {
  getBreakpoints,
  viewportSize
} from './utils.js'


let enableConsoleLogs = false
let logger = (...msg) => enableConsoleLogs && console.log('%cTableOfContents:', 'color:salmon;', ...msg)
let loggerError = (...msg) => enableConsoleLogs && console.error('%cTableOfContents:', 'color:red;', ...msg)


/*
  An enhancement for tables of contents that:
  - highlights toc links when their targets are in the viewport
  - unless all details elements have been manually expanded:
    - automatically opens a details element if it contains any highlighted toc links
    - automatically closes a details element if it does not contain any highlighted toc links
*/


export default class TableOfContents {
  constructor(tocEl) {
    logger(this)

    this.rootEl = tocEl
    // Get links in table of contents:
    this.tocLinks = tocEl.querySelectorAll('a:not([data-toc-heading]):not([href="#top"])')
    // Get breakpoint values:
    try {
      this.tocBreakpoints = getBreakpoints(this.rootEl)
    } catch (error) {
      console.error(error, this.rootEl)
    }

    // Make a custom event to fire if the table of contents'
    // details state changes, so any DetailsToggle instance can
    // update its toggle button contents if necessary
    this.updateDetailsToggleEvent = new CustomEvent('detailstoggle:forceupdate', {
      bubbles: true
    })

    this.handleBreakpointCallback = this.handleBreakpoint.bind(this)
    this.automaticDetailsCallback = this.updateAutomaticDetailsHandling.bind(this)

    if (viewportSize(this.tocBreakpoints.sm, this.tocBreakpoints.lg).isLg.matches) {
      this.setupToc()
    }

    // Watch for viewport changes
    viewportSize(this.tocBreakpoints.sm, this.tocBreakpoints.lg).isLg.addListener(this.handleBreakpointCallback)
  }

  setupToc() {
    logger('setting up toc enhancement')

    // Collect targets of every link:
    this.tocTargets = []

    for (let tocLink of this.tocLinks) {
      if (!document.querySelector(tocLink.hash)) {
        loggerError('Could not find a matching id on this page for this link:', tocLink)
        // If there is a matching id, but the issue is with accented/special characters
        // and their encoding, you will probably want to handle it on the python side
        // rather than messing around with js string normalization.
      } else {
        this.tocTargets.push(document.querySelector(tocLink.hash))
      }
    }

    // Get details elements in table of contents, if any:
    this.tocDetails = this.rootEl.querySelectorAll('details')

    if (this.tocDetails) {
      // Start off by allowing the intersection observer to
      // automatically handle which details are open:
      this.allowAutomaticDetailsHandling = true
      // Instantiate details toggle:
    }

    this.initializeObserver()

    this.rootEl.addEventListener('detailstoggle:opened', this.automaticDetailsCallback)
    this.rootEl.addEventListener('detailstoggle:closed', this.automaticDetailsCallback)
  }

  destroyToc() {
    logger('destroying toc enhancement')

    this.observer.disconnect()

    this.tocLinks.forEach((link) => {
      link.removeAttribute('data-visible')
    })

    this.rootEl.removeEventListener('detailstoggle:opened', this.automaticDetailsCallback)
    this.rootEl.removeEventListener('detailstoggle:closed', this.automaticDetailsCallback)
  }

  initializeObserver() {
    this.observer = this.tocTargetObserver()

    this.tocTargets.forEach(el => {
      this.observer.observe(el)
    })
  }

  tocTargetObserver() {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // logger('observing', entry.target, 'is intersecting?', entry.isIntersecting)

          let tocLinkMatch = [...this.tocLinks].filter(link => {
            return link.hash === `#${entry.target.id}`
          })[0]

          tocLinkMatch.dataset.visible = entry.isIntersecting

          if (this.tocDetails && this.allowAutomaticDetailsHandling) {
            this.updateDetailsState()
          }
        })
      },
      {
        // Register intersection as soon as any part of the target is visible
        threshold: 0,
      }
    )
  }

  updateDetailsState() {
    for (let detailsEl of this.tocDetails) {
      let hasHighlightedLinks = detailsEl.querySelectorAll('[data-visible="true"]')

      if (hasHighlightedLinks.length > 0 && !detailsEl.open) {
        detailsEl.open = true
      }
      if (hasHighlightedLinks.length <= 0 && detailsEl.open) {
        detailsEl.open = false
      }
    }

    this.rootEl.dispatchEvent(this.updateDetailsToggleEvent)
  }

  updateAutomaticDetailsHandling(event) {
    logger('automatic details handler heard detailstoggle event', event)
    if (event.type === 'detailstoggle:opened') {
      // All details elements have been manually opened,
      // so disable automatic opening/closing
      this.allowAutomaticDetailsHandling = false
    }
    if (event.type === 'detailstoggle:closed') {
      // All details elements have been manually closed,
      // so re-enable automatic opening/closing
      this.allowAutomaticDetailsHandling = true
    }
  }

  handleBreakpoint(event) {
    if (event.matches) {
      this.setupToc()
    } else {
      this.destroyToc()
    }
  }

}
